import {useCallback, useEffect} from "react";

const GUARD_STATE_KEY = "__kavBackGuard";

/**
 * Silently neutralises the browser back button while `active` is true.
 *
 * A guard entry is pushed on top of the session history; when the user pops it
 * (back button / swipe back) we immediately push it again, so the URL, the
 * React state and any embedded iframe stay exactly where they were.
 *
 * Returns `pushGuard` so callers can add another guard entry after something
 * else (e.g. an iframe navigation) has appended its own history entry.
 */
const useBlockBrowserBack = (active) => {
    const pushGuard = useCallback(() => {
        if (typeof window === "undefined" || !window.history?.pushState) return;
        try {
            window.history.pushState(
                {...(window.history.state || {}), [GUARD_STATE_KEY]: true},
                "",
                window.location.href
            );
        } catch (_error) {
            // Some embedded browsers throttle pushState; the guard is best-effort.
        }
    }, []);

    useEffect(() => {
        if (!active || typeof window === "undefined") return undefined;

        const onPopState = () => {
            pushGuard();
        };

        pushGuard();
        window.addEventListener("popstate", onPopState);
        return () => {
            window.removeEventListener("popstate", onPopState);
        };
    }, [active, pushGuard]);

    return pushGuard;
};

export default useBlockBrowserBack;
